import React, { useMemo } from 'react';

import { InputMask } from '@react-input/mask';

import { TextInput } from '../TextInput';
import { convertMaskPattern } from './utils';

import type { InputBaseComponentProps } from '@mui/material';
import type { MaskedTextInputProps } from './types';

const MASKED_REPLACEMENT = {
  _: /\d/, // 숫자만 허용
  A: /[A-Za-z]/, // 영문자만 허용
  X: /[A-Za-z0-9]/, // 영문자 + 숫자 허용
} as const;

// InputMask 컴포넌트가 받을 props 타입 정의
interface InputMaskComponentProps extends InputBaseComponentProps {
  mask: string;
  replacement: typeof MASKED_REPLACEMENT | Record<string, RegExp>;
  separate: boolean;
  showMask: boolean;
  modify?: MaskedTextInputProps['modify'];
  track?: MaskedTextInputProps['track'];
}

// 컴포넌트 외부에 정의하여 재생성 방지
const InputMaskComponent = (props: InputBaseComponentProps) => {
  const { inputRef, ...inputProps } = props;
  const maskProps = inputProps as InputMaskComponentProps;
  const { mask, replacement, showMask, separate, track, modify, ...restProps } = maskProps;

  return (
    <InputMask
      ref={inputRef as React.Ref<HTMLInputElement>}
      mask={mask}
      replacement={replacement}
      showMask={showMask}
      separate={separate}
      track={track}
      modify={modify}
      {...restProps}
    />
  );
};

InputMaskComponent.displayName = 'InputMaskComponent';

/**
 * alwaysShowMask 동작을 modify 콜백으로 전환합니다.
 * @description
 * - React 19에서는 @react-input/mask v2가 controlled input을 감지하지 못해(`_wrapperState` 제거),
 *   showMask를 정적으로 켜면 마운트 시점에 빈 input의 value로 mask가 삽입되어 placeholder를 가립니다.
 * - 마운트 시점에는 showMask를 끄고, 입력 tracking 단계에서만 showMask를 켜서
 *   "빈 값 = placeholder, 입력 중 = mask 가이드"를 보장합니다.
 */
const composeShowMaskModify =
  (modify?: MaskedTextInputProps['modify']): NonNullable<MaskedTextInputProps['modify']> =>
  params => ({ showMask: true, ...modify?.(params) });

const MaskedTextInput = (props: MaskedTextInputProps) => {
  const {
    mask = '____.__.__',
    alwaysShowMask = false,
    replacement,
    separate = false,
    track,
    modify,
    inputRef,
    inputProps,
    ref,
    value,
    onChange,
    ...textInputProps
  } = props;

  const convertedMask = useMemo(() => convertMaskPattern(mask), [mask]);

  const maskProps = useMemo(
    () => ({
      mask: convertedMask,
      replacement: replacement || MASKED_REPLACEMENT,
      showMask: false,
      separate,
      track,
      modify: alwaysShowMask ? composeShowMaskModify(modify) : modify,
      ...inputProps,
    }),
    [convertedMask, replacement, alwaysShowMask, separate, track, modify, inputProps],
  );

  return (
    <TextInput
      ref={ref}
      inputRef={inputRef}
      disabledOverflow={true}
      inputComponent={InputMaskComponent}
      inputProps={maskProps}
      value={value}
      onChange={onChange}
      {...textInputProps}
    />
  );
};

MaskedTextInput.displayName = 'MaskedTextInput';

export default MaskedTextInput;
